import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import {
  selectIsBackToBackEyeTest,
  selectTestConfiguration,
  selectTestId,
  selectTestStatus,
} from "@store/tests/testSelectors";
import { selectTestSuiteSessionId } from "@store/testSuite/testSuiteSelectors";
import { selectCompletedTestIds } from "@store/testsession/testSessionSelectors";
import { selectSelectedPatient } from "@store/patients/patientSelectors";
import { TestStatus } from "types/tests/core";
import { getPacsConfig, sendTestToPacs } from "@utils/api-communication/RestApiCli";
import { deriveStudyInstanceUid, getDicomUidRoot } from "@utils/dicom/uid";
import { usePacsFeatureEnabled } from "@utils/dicom/pacsFeature";

const AUTO_SEND_NOTIFICATION_ID = "pacs-auto-send";

export default function AutoSendPacsHost() {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const pacsEnabled = usePacsFeatureEnabled();
  const testStatus = useSelector(selectTestStatus);
  const testId = useSelector(selectTestId);
  const config = useSelector(selectTestConfiguration);
  const patient = useSelector(selectSelectedPatient);
  const suiteSessionId = useSelector(selectTestSuiteSessionId);
  const isBackToBack = useSelector(selectIsBackToBackEyeTest);
  const completedTestIds = useSelector(selectCompletedTestIds);
  const location = useLocation();
  const lastStatus = useRef<TestStatus | null>(null);

  const isAutoSaveIframe =
    new URLSearchParams(location.search).get("autosave") === "1" && window.parent !== window;

  useEffect(() => {
    const prev = lastStatus.current;
    lastStatus.current = testStatus;
    if (prev === null) return;
    if (!pacsEnabled) return;
    if (isAutoSaveIframe) return;
    if (suiteSessionId) return;
    if (prev === TestStatus.Completed || testStatus !== TestStatus.Completed) return;
    if (!testId || !patient?.uuid || !config?.name || !config.testType) return;

    const testIds = [testId];
    if (isBackToBack) {
      const partnerTestId = completedTestIds.find((id) => id && id !== testId);
      if (partnerTestId) testIds.push(partnerTestId);
    }

    void autoSendToPacs(testIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    testStatus,
    pacsEnabled,
    isAutoSaveIframe,
    suiteSessionId,
    isBackToBack,
    completedTestIds,
    testId,
    patient,
    config,
  ]);

  async function autoSendToPacs(testIds: string[]) {
    let pacsConfig;
    try {
      pacsConfig = await getPacsConfig();
    } catch {
      return;
    }
    if (!pacsConfig.store?.enabled || !pacsConfig.store?.autoSend) return;

    let uids: { studyInstanceUid: string; uidRoot: string };
    try {
      const uidRoot = getDicomUidRoot();
      uids = { studyInstanceUid: deriveStudyInstanceUid(uidRoot), uidRoot };
    } catch {
      return;
    }

    notifications.show({
      id: AUTO_SEND_NOTIFICATION_ID,
      loading: true,
      autoClose: false,
      withCloseButton: false,
      title: t("DicomSettings.autoSendSending"),
      message: t("DicomSettings.autoSendSendingMsg"),
    });

    const sendOutcomes = await Promise.allSettled(
      testIds.map((testId) => sendTestToPacs(testId, { uids, objectType: "pdf" })),
    );
    const allSucceeded = sendOutcomes.every(
      (outcome) => outcome.status === "fulfilled" && outcome.value.success,
    );

    notifications.update({
      id: AUTO_SEND_NOTIFICATION_ID,
      loading: false,
      autoClose: 8000,
      withCloseButton: true,
      color: allSucceeded ? "green" : "red",
      title: allSucceeded ? t("DicomSettings.autoSendOk") : t("DicomSettings.autoSendFailed"),
      message: allSucceeded
        ? t("DicomSettings.autoSendOkMsg")
        : t("DicomSettings.autoSendFailedMsg"),
    });
  }

  return null;
}
